import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { MyDashboardTypes } from '../../../../types/my-dashboard.types';

const BLUE = '#206FA5';

interface WorkflowZoneProps {
    onMetricClick: (key: MyDashboardTypes.MetricKey) => void;
}

export const WorkflowZone: React.FC<WorkflowZoneProps> = ({ onMetricClick }) => {
    const summary = useSelector((s: RootState) => s.myDashboard.summary);

    if (!summary) return null;

    // Defensive ?? 0 on every numeric field so a missing/null value during a
    // partial backend rollout doesn't turn anything downstream into NaN.
    const pendingRR = summary.pendingRiskReviews ?? 0;
    // totalRiskReviews from the backend is ALL risk reviews in the window
    // (pending + completed), not just completed. Derive completed = total − pending
    // and use totalRR as the progress-bar denominator. Earlier this added
    // pending on top of total, inflating the denominator and understating %.
    const totalRR = summary.totalRiskReviews ?? 0;
    const completedRR = Math.max(totalRR - pendingRR, 0);
    const appointments = summary.appointments ?? 0;
    const fieldSafetyReports = summary.fieldSafetyReports ?? 0;

    // Only Risk Reviews has a natural "progress" denominator (completed vs
    // total). Appointments and Field Safety Reports are raw counts with no
    // meaningful cap, so rendering a progress bar for them would peg at 100%
    // and mislead operators. `total: null` = render the card without the bar.
    const metrics: {
        label: string;
        key: MyDashboardTypes.MetricKey;
        sub: string;
        value: number;
        total: number | null;
        // Optional override — when set, the progress bar fills from
        // progressValue/total instead of value/total. Used so the hero
        // number can represent total-opened while the bar keeps tracking
        // completion rate.
        progressValue?: number;
        alertLabel?: string;
    }[] = [
        {
            label: 'Visit Risk Reviews',
            key: 'totalRiskReviews',
            // Hero = total opened in the 7-day window; sub-label breaks down
            // completed vs pending; progress bar still tracks closure rate
            // via progressValue. The big number reads as "how many are we
            // handling" rather than "how many are done" — the latter showed
            // as 0 when everything was still in progress, which looked like
            // "no activity" instead of the intended "all still open".
            sub: `${completedRR} completed · ${pendingRR} pending`,
            value: totalRR,
            progressValue: completedRR,
            total: Math.max(totalRR, 1),
            alertLabel: pendingRR > 0 ? `${pendingRR} pending` : undefined,
        },
        {
            label: 'Appointments',
            key: 'appointments',
            sub: `${appointments} this week`,
            value: appointments,
            total: null,
        },
        {
            label: 'Field Safety Reports',
            key: 'fieldSafetyReports',
            sub: `${fieldSafetyReports} filed this week`,
            value: fieldSafetyReports,
            total: null,
        },
    ];

    return (
        <Box sx={zoneStyle}>
            {/* Header */}
            <ZoneHeader label="Workflow" color={BLUE} tag="7D" />

            {/* Metric cards */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                {metrics.map(({ label, key, sub, value, total, progressValue, alertLabel }) => (
                    <Box
                        key={key}
                        onClick={() => onMetricClick(key)}
                        sx={{
                            p: '12px 14px', borderRadius: '10px',
                            bgcolor: `${BLUE}06`,
                            border: '1px solid #E8ECF0',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            '&:hover': { borderColor: BLUE + '40', bgcolor: `${BLUE}0F` },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1A2B3C' }}>{label}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 1.5 }}>
                                {alertLabel && (
                                    <Box sx={{
                                        fontSize: 10, fontWeight: 700, color: BLUE,
                                        bgcolor: `${BLUE}15`, border: `1px solid ${BLUE}30`,
                                        px: '7px', py: '2px', borderRadius: 10, whiteSpace: 'nowrap',
                                    }}>
                                        {alertLabel}
                                    </Box>
                                )}
                                <Typography sx={{ fontSize: 22, fontWeight: 800, color: BLUE }}>{value}</Typography>
                            </Box>
                        </Box>
                        <Typography sx={{ fontSize: 11, color: '#94A3B8', mb: total !== null ? 1.25 : 0 }}>{sub}</Typography>
                        {total !== null && (
                            <LinearProgress
                                variant="determinate"
                                value={total > 0 ? Math.round(((progressValue ?? value) / total) * 100) : 0}
                                sx={{
                                    height: 5, borderRadius: 3,
                                    bgcolor: `${BLUE}18`,
                                    '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: BLUE },
                                }}
                            />
                        )}
                    </Box>
                ))}
            </Box>

        </Box>
    );
};

function ZoneHeader({ label, color, tag }: { label: string; color: string; tag: string }) {
    return (
        <Box sx={{ mb: 2.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 3, height: 16, borderRadius: 1, bgcolor: color }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        {label}
                    </Typography>
                </Box>
                <Box sx={{
                    fontSize: 10, fontWeight: 700, color,
                    bgcolor: `${color}12`, border: `1px solid ${color}30`,
                    px: 1, py: 0.25, borderRadius: 10,
                }}>
                    {tag}
                </Box>
            </Box>
        </Box>
    );
}

const zoneStyle = {
    flex: '1 1 280px',
    minWidth: 260,
    bgcolor: '#fff',
    border: '1px solid #E8ECF0',
    borderRadius: '14px',
    p: '22px 22px 16px',
    display: 'flex',
    flexDirection: 'column' as const,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};
